import { AnnotationEvent } from './../../models/Events';
import { PdfInstanceManagerService } from './instance/pdf-instance-manager.service';
import { Injectable } from '@angular/core';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { TabService } from './tab.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { DocumentMarkersMode, AnnotationTypes } from 'src/app/core/pdf-utils/enums';
import { IMarkerColor, IMarkerContent } from 'src/app/core/interfaces';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { DocumentManagerService } from './document/document-manager.service';
import { CurrentUiStoreService } from './ui/current-ui-store.service';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { CurrentUIState } from 'src/models/UIState';
import { DocumentMarkersService } from './document/document-markers.service';
import { isAnnotationType } from '../core/common';
import { ReadingModesService } from './reading-modes.service';
import { ReadingMode } from '../core/reading-utils/enums';
import { environment } from 'src/environments/environment';
import { Core } from '@pdftron/webviewer';
import { AsgardDocEpub } from '../core/documents/asgard-doc-epub';
import { EpubAnnotationService } from './epub/epub-annotations.service';
import { mapToEpubAnnotationMode } from '../core/epub-utils/mappers';

@Injectable({
  providedIn: 'root'
})
export class MarkersBarService {
  private modeActive: DocumentMarkersMode = DocumentMarkersMode.None;
  private color: IMarkerColor;
  private modeActiveSubject: BehaviorSubject<DocumentMarkersMode>;
  private colorSubject: BehaviorSubject<IMarkerColor>;

  private get currentDoc(): AsgardDocBase | null | undefined { return this.docMgrService.currentDoc; }

  constructor(
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly docMgrService: DocumentManagerService,
    private readonly docMarkersService: DocumentMarkersService,
    private readonly tabService: TabService,
    private readonly uiService: CurrentUiStoreService,
    private readonly readingModesService: ReadingModesService,
    private readonly epubAnnotationsService: EpubAnnotationService,
  ) {
    this.modeActiveSubject = new BehaviorSubject(this.modeActive);
    this.colorSubject = new BehaviorSubject(this.color);
    this.initSubscriptions();
  }

  private get isInEraserMode(): boolean {
    return this.modeActive === DocumentMarkersMode.Eraser;
  }

  private get isInMarkerMode(): boolean {
    return this.modeActive === DocumentMarkersMode.Marker;
  }

  private setActiveMode(mode: DocumentMarkersMode): void {

    if (this.modeActive === mode) {
      return;
    }

    this.modeActive = mode;
    this.modeActiveSubject.next(mode);
    if (this.isInEditingMode) {
      this.uiService.setItemActive(AsgardFeatureNames.MARKERS);
    } else {
      this.uiService.setItemUnActive(AsgardFeatureNames.MARKERS);
    }
    if (this.currentDoc && this.currentDoc instanceof AsgardDocPdf) {
      this.docMarkersService.setMarkerMode(mode);
    } else if (this.currentDoc && this.currentDoc instanceof AsgardDocEpub) {
      this.epubAnnotationsService.setAnnotationMode(mapToEpubAnnotationMode(mode));
    }
  }

  private setActiveColor(color: IMarkerColor): void {
    this.color = color;
    this.colorSubject.next(color);
    if (this.currentDoc && this.currentDoc instanceof AsgardDocPdf) {
      this.docMarkersService.setMarkerColor(color);
    } else if (this.currentDoc && this.currentDoc instanceof AsgardDocEpub) {
      this.epubAnnotationsService.setHighlightColor(color);
    }
  }

  public get isInEditingMode(): boolean {
    return this.isInMarkerMode || this.isInEraserMode;
  }

  public getActiveColorSub(): Observable<IMarkerColor> {
    return this.colorSubject.asObservable();
  }

  public getActiveModeSub(): Observable<DocumentMarkersMode> {
    return this.modeActiveSubject.asObservable();
  }

  public toggleColor(color: IMarkerColor): void {
    if (this.isInMarkerMode && this.color.hex === color.hex) {
      this.setActiveMode(DocumentMarkersMode.None);
      return;
    }

    this.setActiveMode(DocumentMarkersMode.Marker);
    this.setActiveColor(color);
  }

  public toggleEraser(): void {
    this.setActiveMode(
      this.isInEraserMode ? DocumentMarkersMode.None : DocumentMarkersMode.Eraser
    );
  }

  public resetState(): void {
    this.setActiveMode(DocumentMarkersMode.None);
  }

  public async extractMarkers(withColors: boolean = false, withFormatting: boolean = true, sortByColor: boolean = true): Promise<void> {
    if (!this.currentDoc) return;

    let markers;

    if (this.currentDoc instanceof AsgardDocPdf) {
      markers = await this.docMarkersService.getMarkerAnnotations();
    } else if (this.currentDoc instanceof AsgardDocEpub) {
      markers = await this.epubAnnotationsService.getMarkers();
    } else {
      return;
    }

    if (!markers.length) {
      return;
    }

    if (sortByColor) {
      markers = this.sortSelectedText(markers, 'color');
    }
    const editorTab = this.tabService.addNewTab();
    const subscription = this.docMgrService.documentReady.subscribe(x => {
      if (x === editorTab.id) {
        markers.forEach(m => {
          // console.log('marker', m);
          // HOT FIX
          // TODO:: HANDLE EXTRACTION PROPERLY
          // FOR NOW JUST INGORE WHITE COLOR
          if (m.color !== '#ffffff') {
            (this.currentDoc as AsgardDocFroala).insertMarkedText(m, withColors, withFormatting);
          }
        });
        subscription.unsubscribe();
      }
    });
  }

  sortSelectedText(data: IMarkerContent[], property: string, direction?: number) {
    const colorsSortingOrder = environment.highlight.supportedColors.map(color => color.hex.toLowerCase());
    direction = direction || 1;
    data.sort((a, b) => {
      return colorsSortingOrder.indexOf(a[property]) - colorsSortingOrder.indexOf(b[property]);
    });
    return data;
  }

  private initSubscriptions(): void {
    this.uiService.getUIState().subscribe((e: CurrentUIState) => {
      if (!e[AsgardFeatureNames.MARKERS].active) {
        this.resetState();
      }
    });

    this.pdfInstanceManager.annotationAdded.subscribe((e: AnnotationEvent<Core.Annotations.TextHighlightAnnotation>) => {
      if (this.isInEditingMode && e && e.annotations && e.annotations.length) {
        const anot = e.annotations[0];
        if (isAnnotationType(anot, [AnnotationTypes.Marker])) {
          this.docMarkersService.addMarkerHandler(anot);
        }
      }
    });

    // this.pdfInstanceManager.annotationsLoaded.subscribe(() => {
    //   this.docMarkersService.initExistingAnnotationsHandler();
    // });

    this.readingModesService.globalReadingModeObservable.subscribe((mode: ReadingMode) => {
      if (this.currentDoc && this.currentDoc instanceof AsgardDocPdf && this.currentDoc.isDocumentReady) {
        if (mode === ReadingMode.Selection || mode === ReadingMode.RectangleSelection) {
          this.uiService.setItemUnActive(AsgardFeatureNames.HIGHLIGHT_MENU);
          this.uiService.disableState(AsgardFeatureNames.HIGHLIGHT_MENU);
        } else {
          this.resetState();
          this.uiService.enableState(AsgardFeatureNames.HIGHLIGHT_MENU);
        }
      }
    });

    this.docMgrService.documentReady.subscribe(() => {
      if (this.currentDoc && this.tabService.activeTab &&
        this.currentDoc instanceof AsgardDocPdf && this.currentDoc.documentId === this.tabService.activeTab.id) {
        if (this.readingModesService.readingMode === ReadingMode.Selection) {
          this.uiService.setItemUnActive(AsgardFeatureNames.HIGHLIGHT_MENU);
          this.uiService.disableState(AsgardFeatureNames.HIGHLIGHT_MENU);
        }
      }
    });

    this.docMgrService.currentDocumentChanged.subscribe(() => {
      if (this.currentDoc && this.currentDoc instanceof AsgardDocPdf && this.currentDoc.isDocumentReady) {
        if (this.readingModesService.readingMode === ReadingMode.Selection) {
          this.uiService.setItemUnActive(AsgardFeatureNames.HIGHLIGHT_MENU);
          this.uiService.disableState(AsgardFeatureNames.HIGHLIGHT_MENU);
        }
      }
    });
  }
}
